import React, { useState } from "react";
import { CircularProgress } from "@mui/material";
import { CarOrderControlsProps } from "../utils/types";
import SearchIcon from "@mui/icons-material/Search";
import ImportButton from "src/business-types/saloon/modules/orders/components/orders/order-table/order-actions/ImportButton";

const dateOptions = ["Today", "Yesterday", "Last Week", "Last Month", "180 days", "CustomizeDate"];
const exportOptions = ["csv", "pdf"];

const CarOrderControls: React.FC<CarOrderControlsProps> = ({
  filter,
  setFilter,
  selectedDate,
  setSelectedDate,
  selectedDate2,
  setSelectedDate2,
  searchQuery,
  handleSearchChange,
  exportFormat,
  handleDownload,
  loadingExport,
}) => {
  const [importLoading, setImportLoading] = useState(false);

  const handleImport = async () => {
    try {
      setImportLoading(true);
      await new Promise((resolve) => setTimeout(resolve, 2000));
    } catch (error) {
      console.error("Import failed:", error);
    } finally {
      setImportLoading(false);
    }
  };

  return (
    <div className="w-full bg-gradient-to-r from-blue-50 to-teal-50 rounded-2xl shadow-md p-4 mb-0 border border-blue-100">
      <div className="flex flex-wrap items-end justify-between gap-6">
        {/* Filter Section */}
        <div className="flex flex-col min-w-[200px]">
          <label className="text-sm font-semibold text-gray-700 mb-2">Choose an option</label>
          <select
            value={filter}
            onChange={(e) => setFilter(e.target.value)}
            className="border border-blue-200 bg-white text-gray-700 rounded-xl px-3 py-2 shadow-sm focus:ring-2 focus:ring-blue-400 focus:outline-none hover:border-blue-300 transition-all duration-150"
          >
            <option value="" disabled>
              Select a date range
            </option>
            {dateOptions.map((option) => (
              <option key={option} value={option}>
                {option}
              </option>
            ))}
          </select>
        </div>

        {/* Import Section */}
        <div className="flex flex-col min-w-[200px] relative">
          <label className="text-sm font-semibold text-gray-700 mb-2">Import Orders</label>
          <div className="flex items-center gap-3">
            <ImportButton onImport={handleImport} onLoading={setImportLoading} />
            {importLoading && <CircularProgress size={22} />}
          </div>
        </div>

        {/* Export Section */}
        <div className="flex flex-col min-w-[200px] relative">
          <label className="text-sm font-semibold text-gray-700 mb-2">Export as</label>
          <select
            value={exportFormat}
            onChange={handleDownload}
            className="border border-blue-200 bg-white text-gray-700 rounded-xl px-3 py-2 shadow-sm focus:ring-2 focus:ring-teal-400 focus:outline-none hover:border-teal-300 transition-all duration-150"
          >
            <option value="" disabled>
              Select format
            </option>
            {exportOptions.map((option) => (
              <option key={option} value={option}>
                {option.toUpperCase()}
              </option>
            ))}
          </select>
          {loadingExport && (
            <div className="absolute right-3 top-8">
              <CircularProgress size={22} />
            </div>
          )}
        </div>

        {/* Search Section with Floating Label */}
        <div className="flex-1 min-w-[300px] relative">
          <div className="relative">
            <SearchIcon className="absolute left-3 top-2.5 text-blue-500" />
            <input
              id="Search-Phone-number"
              placeholder="Search by Name, Phone, or Order ID"
              type="tel"
              value={searchQuery}
              onChange={handleSearchChange}
              className="w-full pl-10 pr-3 py-2.5 border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 placeholder-gray-400 text-sm text-black bg-white"
            />
          </div>
        </div>

        {/* Custom Date Range Section */}
        {filter === "CustomizeDate" && (
          <div className="flex flex-col sm:flex-row items-center gap-4 min-w-[260px] bg-white rounded-xl border border-blue-100 p-3 shadow-sm mt-2">
            <div className="flex flex-col">
              <label htmlFor="from" className="text-sm font-semibold text-gray-700 mb-1">
                From
              </label>
              <input
                id="from"
                type="date"
                value={selectedDate}
                onChange={(e) => setSelectedDate(e.target.value)}
                className="border border-blue-200 rounded-xl px-3 py-2 focus:ring-2 focus:ring-blue-400 focus:outline-none text-gray-700"
              />
            </div>

            <div className="flex flex-col">
              <label htmlFor="to" className="text-sm font-semibold text-gray-700 mb-1">
                To
              </label>
              <input
                id="to"
                type="date"
                value={selectedDate2}
                onChange={(e) => setSelectedDate2(e.target.value)}
                className="border border-blue-200 rounded-xl px-3 py-2 focus:ring-2 focus:ring-blue-400 focus:outline-none text-gray-700"
              />
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default CarOrderControls;
